import { useState, useRef } from 'react';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { SearchIcon, ZoomOutMapIcon, FilterAltIcon } from '@/components/ui/Icons';
import { ColumnWidthPopover } from './ColumnWidthPopover';
import { FilterPopover } from './FilterPopover';
import { ColumnSearchPopover } from './ColumnSearchPopover';
import { useProjectsColumnSearch } from '@/contexts/ProjectsColumnSearchContext';
import type { FilterRule, FilterColumnType } from '@/lib/tableFilters';
import type { Project } from '@/api/client';
import './HeaderCellWithWidth.css';

export interface FilterCellProps {
  filterType: FilterColumnType;
  currentRule: FilterRule | null;
  onFilterChange: (columnId: string, rule: FilterRule | null) => void;
  allProjects: Project[];
}

interface HeaderCellWithWidthProps {
  columnId: string;
  width: string;
  defaultWidth: string;
  onWidthChange: (columnId: string, width: string) => void;
  children: React.ReactNode;
  /** Скрыть кнопку и попап ширины (фиксированная ширина столбца). */
  widthAdjustDisabled?: boolean;
  /** Заголовок для попапа поиска по столбцу (при наличии filterProps). */
  searchPopoverTitle?: string;
  /** Если передано — показывается кнопка фильтра. */
  filterProps?: FilterCellProps;
  /** Глобальный тоггл фильтров. Если false — кнопка фильтра скрыта (кроме активного фильтра). */
  filtersEnabled?: boolean;
}

export function HeaderCellWithWidth({
  columnId,
  width,
  defaultWidth,
  onWidthChange,
  children,
  widthAdjustDisabled,
  filterProps,
  filtersEnabled,
  searchPopoverTitle,
}: HeaderCellWithWidthProps) {
  const {
    scopeId: columnSearchScopeId,
    debouncedColumnSearch,
    beginColumnSearchScope,
    clearColumnSearch,
  } = useProjectsColumnSearch();
  const [widthPopoverOpen, setWidthPopoverOpen] = useState(false);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [columnSearchPopoverOpen, setColumnSearchPopoverOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const cellRef = useRef<HTMLDivElement>(null);
  const columnSearchPopoverRef = useRef<HTMLDivElement>(null);

  const hasActiveFilter = !!filterProps?.currentRule;
  const showFilterBtn = !!filterProps && (filtersEnabled || hasActiveFilter);
  const searchTitle = searchPopoverTitle ?? columnId;
  const showColumnSearchBtn = !!filterProps;
  const isThisColumnSearchActive =
    columnSearchScopeId === columnId && debouncedColumnSearch.length > 0;
  const controlsVisible =
    hovered || hasActiveFilter || isThisColumnSearchActive || columnSearchPopoverOpen;

  return (
    <div
      ref={cellRef}
      className="header-cell-with-width"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}

      <div className={`header-cell-with-width__controls ${controlsVisible ? 'header-cell-with-width__controls_visible' : ''}`}>
        {showFilterBtn && (
          <button
            type="button"
            className={`header-cell-with-width__btn header-cell-with-width__btn_filter${hasActiveFilter ? ' header-cell-with-width__btn_filter-active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setFilterPopoverOpen(true);
            }}
            title="Фильтр"
            aria-label="Фильтр"
          >
            <FilterAltIcon size={12} />
            {hasActiveFilter && <span className="header-cell-with-width__filter-dot" />}
          </button>
        )}
        {showColumnSearchBtn && (
          <button
            type="button"
            className={`header-cell-with-width__btn header-cell-with-width__btn_search${
              isThisColumnSearchActive ? ' header-cell-with-width__btn_search-active' : ''
            }`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              beginColumnSearchScope(columnId, [columnId]);
              setColumnSearchPopoverOpen(true);
            }}
            title="Поиск по столбцу"
            aria-label="Поиск по столбцу"
          >
            <SearchIcon size={12} />
          </button>
        )}
        {showColumnSearchBtn && isThisColumnSearchActive && (
          <button
            type="button"
            className="header-cell-with-width__btn header-cell-with-width__btn_column-search-clear"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              clearColumnSearch();
            }}
            title="Сбросить поиск по столбцу"
            aria-label="Сбросить поиск по столбцу"
          >
            <SearchOffIcon sx={{ fontSize: 12 }} />
          </button>
        )}
        {!widthAdjustDisabled && (
          <button
            type="button"
            className="header-cell-with-width__btn"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setWidthPopoverOpen(true);
            }}
            title="Ширина"
            aria-label="Ширина"
          >
            <ZoomOutMapIcon size={12} />
          </button>
        )}
      </div>

      {!widthAdjustDisabled && (
        <ColumnWidthPopover
          columnId={columnId}
          currentWidth={width}
          defaultWidth={defaultWidth}
          onWidthChange={onWidthChange}
          triggerRef={cellRef}
          isOpen={widthPopoverOpen}
          onClose={() => {
            setWidthPopoverOpen(false);
            setHovered(false);
          }}
        />
      )}

      {filterProps && (
        <FilterPopover
          columnId={columnId}
          filterType={filterProps.filterType}
          currentRule={filterProps.currentRule}
          onApply={filterProps.onFilterChange}
          triggerRef={cellRef}
          isOpen={filterPopoverOpen}
          onClose={() => {
            setFilterPopoverOpen(false);
            setHovered(false);
          }}
          allProjects={filterProps.allProjects}
        />
      )}
      <ColumnSearchPopover
        title={searchTitle}
        isOpen={columnSearchPopoverOpen}
        onClose={() => {
          setColumnSearchPopoverOpen(false);
          setHovered(false);
        }}
        triggerRef={cellRef}
        popoverRef={columnSearchPopoverRef}
      />
    </div>
  );
}
